'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { getRuntimeOrg, asset } from '@/utils/runtimeOrg';

type Theme = Record<string, string>;
type ThemePack = { light?: Theme; dark?: Theme };
type Props = {
  name?: string;
  favicon?: string; // e.g. 'favicon-128.png' or '/bomet/favicon-128.png'
  theme?: ThemePack; // { light: { primary: '#...' }, dark: {...} }
  themeMode?: 'light' | 'dark';
};

const ThemeLoader = ({ name, favicon, theme, themeMode = 'light' }: Props) => {
  const pathname = usePathname();

  useEffect(() => {
    const org = getRuntimeOrg();
    const root = document.documentElement;

    // data attrs for debugging/usage
    root.setAttribute('data-org', org);
    root.setAttribute('data-theme', themeMode);

    // apply CSS vars
    const colors = (theme?.[themeMode] ?? {}) as Record<string, string>;
    for (const [k, v] of Object.entries(colors)) {
      root.style.setProperty(`--color-${k}`, String(v));
    }

    // tab title
    if (name) document.title = name;

    // description stays owned by Metadata; skip or set if you want:
    // upsertMeta('description', description);

    // favicon per org (prefix-aware)
    if (favicon) {
      const href = asset(favicon, org);
      const rels = ['icon', 'shortcut icon', 'apple-touch-icon'];
      for (const rel of rels) {
        let link = document.querySelector<HTMLLinkElement>(
          `link[rel="${rel}"]`
        );
        if (!link) {
          link = document.createElement('link');
          link.rel = rel;
          document.head.appendChild(link);
        }
        link.href = href;
      }
    }
  }, [pathname, name, favicon, theme, themeMode]);

  return null;
};

export default ThemeLoader;
